import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [listingId, setListingId] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setListingId(data.listing_id);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
        tries.current += 1;
        if (tries.current > 30) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        tries.current += 1;
        if (tries.current > 30) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center fade-in">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
          <h1 className="font-display text-3xl mt-6">{t("payment.processing_title")}</h1>
          <p className="text-muted-foreground mt-2">{t("payment.processing_sub")}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-primary" />
          </div>
          <h1 className="font-display text-3xl mt-6">{t("payment.success_title")}</h1>
          <p className="text-muted-foreground mt-2">{t("payment.success_sub")}</p>
          <div className="mt-8 flex flex-col gap-3">
            <Link to="/messages" data-testid="success-messages">
              <Button className="w-full rounded-full">{t("payment.open_messages")}</Button>
            </Link>
            <Link to="/" data-testid="success-home">
              <Button variant="outline" className="w-full rounded-full">{t("payment.continue_shopping")}</Button>
            </Link>
          </div>
        </>
      )}
      {status === "failed" && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/15 flex items-center justify-center">
            <XCircle className="w-9 h-9 text-destructive" />
          </div>
          <h1 className="font-display text-3xl mt-6">{t("payment.failed_title")}</h1>
          <p className="text-muted-foreground mt-2">{t("payment.failed_sub")}</p>
          <div className="mt-8">
            {listingId ? (
              <Link to={`/item/${listingId}`}><Button className="rounded-full">{t("payment.back_listing")}</Button></Link>
            ) : (
              <Link to="/"><Button className="rounded-full">{t("payment.back_home")}</Button></Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useTranslation();
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center fade-in">
      <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
        <XCircle className="w-9 h-9 text-muted-foreground" />
      </div>
      <h1 className="font-display text-3xl mt-6">{t("payment.cancel_title")}</h1>
      <p className="text-muted-foreground mt-2">{t("payment.cancel_sub")}</p>
      <Link to="/" className="inline-block mt-8">
        <Button className="rounded-full">{t("payment.continue_shopping")}</Button>
      </Link>
    </div>
  );
}
